import React, { useState } from "react";

import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import Toast3 from "../../../components/Toast3";
import ProfileBg from "../../../images/profile-bg.jpg";

import { ConnectionButton } from "@shared/components/ConnectionButton";
import theme from "@shared/utils/themeColors";
import { useImageUpload } from "../../../shared/hooks/useImageUpload";
import {
  getProfileThumbnail,
  useProfile,
} from "../../../shared/hooks/useProfile";
import { UserProfile } from "../../../shared/types/profile.types";
import { DateFormat, formatDate } from "../../../shared/utils/date.utils";
import ConnectionsTab from "./ConnectionsTab";
import UserTestCard from "./UserTestCard";

interface ProfileCardProps {
  profile: UserProfile;
  onRefresh?: () => Promise<any>;
  setProfileSidebarOpen?: (open: boolean) => void;
  profileSidebarOpen?: boolean;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  onRefresh,
  setProfileSidebarOpen = undefined,
  profileSidebarOpen = false,
}) => {
  const { name, summary, aboutMe, title, location } = profile;

  const { profile: currentUserProfile } = useProfile();

  const { uploadImage, isUploading } = useImageUpload({
    type: "background",
    onSuccess: () => {
      toast.success("Background image updated");
    },
    onError: (error) => {
      toast.error("Error updating background image");
      console.error("Error uploading background:", error);
    },
  });

  const handleBackgroundUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await uploadImage(file);
      // Refetch profile data after successful upload
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error("Error handling background upload:", error);
    }
  };

  const hasSidebar = setProfileSidebarOpen !== undefined;

  const handleBack = () => {
    if (hasSidebar) {
      setProfileSidebarOpen(true);
      return;
    }
    window.history.back();
  };

  const [activeTab, setActiveTab] = useState<"about" | "connections">("about");

  // Tabs navigation
  const tabs = [
    { id: "about", label: "About" },
    { id: "connections", label: "Connections" },
  ];

  return (
    <div
      className={`grow flex flex-col md:translate-x-0 transition-transform duration-300 ease-in-out ${
        profileSidebarOpen ? "translate-x-1/3" : "translate-x-0"
      }`}
    >
      {/* Profile background */}
      <div className="relative h-56 bg-gray-200 dark:bg-gray-900">
        <img
          className="object-cover h-full w-full"
          src={profile?.thumbnailProfileBgUrl || ProfileBg}
          width="979"
          height="220"
          alt="Profile background"
        />
        {/* Close button */}
        <button
          className="absolute top-4 left-4 sm:left-6 text-white opacity-80 hover:opacity-100"
          aria-controls="profile-sidebar"
          type="button"
          onClick={handleBack}
        >
          <span className="sr-only">Close sidebar</span>
          <svg
            className="w-6 h-6 fill-current"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
          </svg>
        </button>

        {isUploading && (
          <div className="absolute inset-0 bg-white bg-opacity-75  flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          </div>
        )}

        {/* Upload background button */}
        {currentUserProfile?.id === profile.id && (
          <label
            htmlFor="background-upload"
            className="absolute bottom-4 z-10 right-4 bg-white bg-opacity-90 dark:bg-gray-800 dark:bg-opacity-90 text-gray-700 dark:text-gray-300 rounded-lg px-4 py-2 cursor-pointer hover:bg-opacity-100 dark:hover:bg-opacity-100 transition-all duration-200 flex items-center gap-2 btn-sm"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Change background
          </label>
        )}
        <input
          type="file"
          id="background-upload"
          className="hidden"
          accept="image/*"
          onChange={handleBackgroundUpload}
        />
      </div>

      {/* Content */}
      <div className="relative px-4 sm:px-6 pb-8">
        {/* Pre-header */}
        <div className="-mt-16 mb-6 sm:mb-3">
          <div className="flex flex-col items-center sm:flex-row sm:justify-between sm:items-end">
            {/* Avatar */}
            <div className="inline-flex -ml-1 -mt-1 mb-4 sm:mb-0">
              <img
                className="rounded-full border-4 border-white dark:border-gray-900"
                src={getProfileThumbnail(profile, name)}
                width="128"
                height="128"
                alt="Avatar"
              />
            </div>

            {/* Actions */}
            <div className="flex space-x-2 sm:mb-2">
              {currentUserProfile?.id !== profile.id ? (
                <>
                  <div className="mt-4">
                    <ConnectionButton userId={profile.id} variant="profile" />
                  </div>
                </>
              ) : (
                <Link
                  to="/settings/account"
                  className={`btn-sm ${theme.bg.primary} border border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm`}
                >
                  <svg
                    className={`fill-current text-gray-400 dark: ${theme.text.muted}`}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                  >
                    <path d="M11.7.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM4.6 14H2v-2.6l6-6L10.6 8l-6 6zM12 6.6L9.4 4 11 2.4 13.6 5 12 6.6z" />
                  </svg>
                  <span className="ml-2">Edit Profile</span>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Header */}
        <header className="text-center sm:text-left mb-6">
          {/* Name */}
          <div className="inline-flex items-start mb-2">
            <h1 className={`text-2xl ${theme.text.primary} font-bold`}>
              {name}
            </h1>
          </div>
          {/* Bio */}
          <div className="text-sm mb-3">{summary}</div>
          {/* Meta */}
          <div className="flex flex-wrap justify-center sm:justify-start space-x-4">
            {location && (
              <div className="flex items-center">
                <svg
                  className={`fill-current shrink-0 text-gray-400 dark: ${theme.text.muted}`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 8.992a2 2 0 1 1-.002-3.998A2 2 0 0 1 8 8.992Zm-.7 6.694c-.1-.1-4.2-3.696-4.2-3.796C1.7 10.69 1 8.892 1 6.994 1 3.097 4.1 0 8 0s7 3.097 7 6.994c0 1.898-.7 3.697-2.1 4.996-.1.1-4.1 3.696-4.2 3.796-.4.3-1 .3-1.4-.1Zm-2.7-4.995L8 13.688l3.4-2.997c1-1 1.6-2.198 1.6-3.597 0-2.798-2.2-4.996-5-4.996S3 4.196 3 6.994c0 1.399.6 2.698 1.6 3.697 0-.1 0-.1 0 0Z" />
                </svg>
                <span
                  className={`text-sm font-medium whitespace-nowrap ${theme.text.muted}`}
                >
                  {location}
                </span>
              </div>
            )}
          </div>
        </header>

        {/* Tabs */}
        <div className="relative mb-6">
          <div
            className={`absolute bottom-0 w-full h-px ${theme.bg.tertiary} /60`}
            aria-hidden="true"
          ></div>
          <ul className="relative text-sm font-medium flex flex-nowrap -mx-4 sm:-mx-6 lg:-mx-8 overflow-x-scroll no-scrollbar">
            {tabs.map((tab) => (
              <li
                key={tab.id}
                className="mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8"
              >
                <button
                  className={`block pb-3 whitespace-nowrap ${
                    activeTab === tab.id
                      ? `${theme.text.brand} border-b-2 ${theme.border.brand}`
                      : `${theme.text.muted} dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300`
                  }`}
                  onClick={() =>
                    setActiveTab(tab.id as "about" | "connections")
                  }
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Profile content */}
        {activeTab === "about" ? (
          <div className="flex flex-col xl:flex-row xl:space-x-16">
            {/* Main content */}
            <div className="flex-1 space-y-5 mb-8 xl:mb-0">
              {/* About Me */}
              <div>
                <h2 className={`${theme.text.primary} font-semibold mb-2`}>
                  About Me
                </h2>
                <div className="text-sm space-y-2">
                  {aboutMe?.split("\n").map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>

              {/* Test History */}
              <div className={`py-5 border-t ${theme.border.primary}`}>
                <h2 className={`${theme.text.primary} font-semibold mb-2`}>
                  Test History
                </h2>
                {!profile.tests || profile.tests.length === 0 ? (
                  <Toast3
                    open={true}
                    setOpen={() => {}}
                    className={undefined}
                    type={undefined}
                    closable={false}
                  >
                    No tests taken yet.
                  </Toast3>
                ) : (
                  <div className="grid grid-cols-12 gap-6" key={profile.id}>
                    {profile.tests.map((test) => (
                      <UserTestCard key={test.id} test={test} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="xl:min-w-56 xl:w-56 space-y-3">
              <div className="text-sm">
                <h3 className={`font-medium ${theme.text.primary}`}>Title</h3>
                <div>{title}</div>
              </div>
              <div className="text-sm">
                <h3 className={`font-medium ${theme.text.primary}`}>
                  Location
                </h3>
                <div>{location}</div>
              </div>
              <div className="text-sm">
                <h3 className={`font-medium ${theme.text.primary}`}>
                  Birthdate
                </h3>
                <div>{formatDate(profile.birthday, DateFormat.MEDIUM)}</div>
              </div>

              <div className="text-sm">
                <h3 className={`font-medium ${theme.text.primary}`}>Joined</h3>
                <div>
                  {profile.createdAt &&
                    formatDate(profile.createdAt, DateFormat.MEDIUM)}
                </div>
              </div>
            </aside>
          </div>
        ) : (
          <ConnectionsTab userId={profile.id} />
        )}
      </div>
    </div>
  );
};

export default ProfileCard;
